import { SectionDescription } from "components/CustomQuote/DeclineRequestModal/DeclineRequestModal.styles";
import { DetailsBox, DetailsBoxTitle, SectionTitle } from "../../Checkout.styles";
import useMarketingMaterialsCart from "features/MarketingMaterials/hooks/useMarketingMaterialsCart.ts";
import { useGetUserDetailsQuery } from "features/api/userApi.ts";
import {
	GeneratingPDFText,
	PDFDownloadText,
	PDFErrorText,
	TotalItemsText,
	UnavailableDataPlaceholderText
} from "constants/text";
import { Divider } from "@mui/material";
import { formatAddress } from "utils/address.ts";
import { CartItemDetails } from "../CartItemDetails";
import { displayBillToInfo, formatAccountName } from "utils/accounts.ts";
import {
	AccountBillToBox,
	DetailsHeaderContent,
	OrderDetailsHeaderRow,
	ReviewShippingDetails,
	ReviewShippingDetailsRow
} from "features/MarketingMaterials/pages/Checkout/components/ReviewCartDetails/ReviewCartDetails.styles.ts";
import { dateFormatTwoDigitDays } from "utils/date.ts";
import { ConfigurationGroup, todayDate } from "components/NewOrders/utils/NewOrderUtils.tsx";
import useNewOrdersPDFGenerator from "hooks/useNewOrdersPDFGenerator.ts";
import useSnackbarEffect from "hooks/useSnackbarEffect.ts";
import ExportButtons from "components/ExportFile/ExportButtons.tsx";
import Snackbar from "components/Common/SnackBar/SnackBar.tsx";
import { OrderType } from "types/declarations/enums.ts";
import { RootState } from "stores/application.store.tsx";
import { useSelector } from "react-redux";
import { models } from "types/api/viewModels.ts";

interface ReviewCartDetailsProps {
	isConfirmationPage?: boolean;
	cartItems: models["ShoppingCartItem"][];
}

export const ReviewCartDetails = ({ cartItems, isConfirmationPage = false }: ReviewCartDetailsProps) => {
	const { shippingDetails, selectedMerchAccount, getTotalPrice } = useMarketingMaterialsCart();
	const shippingAddress = shippingDetails.address?.address;
	const { data: user } = useGetUserDetailsQuery();
	const confirmationNumber = useSelector((state: RootState) => state.marketingMaterialsCart.confirmationNumber);
	const totalItems = cartItems.reduce((total, item) => total + (item.quantityOrdered ?? 0), 0);

	const { pdfData, generateNewOrdersPDF } = useNewOrdersPDFGenerator(
		null, // grouped configs
		"Confirmation",
		getTotalPrice(),
		{
			orderType: OrderType.MERCH,
			poNumber: shippingDetails?.poNumber,
			jobName: shippingDetails?.jobName,
			shipToAddress: {
				...shippingAddress,
				email: shippingDetails?.email,
				phoneNumber: shippingDetails?.phoneNumber
			},
			confirmationNumber
		}, // newOrderDetails
		!isConfirmationPage,
		isConfirmationPage,
		{}, // orderDetail
		false,
		false,
		undefined,
		undefined,
		user?.role,
		cartItems,
		{
			accountId: selectedMerchAccount?.accountId,
			accountName: selectedMerchAccount?.accountName,
			accountNumber: selectedMerchAccount?.accountNumber,
			billToId: selectedMerchAccount?.billTos?.[0]?.billToId,
			globals: { productLine: { code: "MERCH" } }
		} as ConfigurationGroup // format as merch configuration group
	);
	const { snackbarOpen } = useSnackbarEffect(pdfData.loading);

	return (
		<>
			<DetailsBox>
				<OrderDetailsHeaderRow>
					<DetailsBoxTitle>Order Details</DetailsBoxTitle>
					{isConfirmationPage && (
						<div>
							<ExportButtons
								largePDF={false}
								generatePDF={generateNewOrdersPDF}
								buttonHeader="EXPORT CONFIRMATION"
								isOnlyPDF
							/>
							<Snackbar
								open={snackbarOpen}
								isLoading={pdfData.loading}
								loadingText={GeneratingPDFText}
								successText={PDFDownloadText}
								errorText={PDFErrorText}
								isError={Boolean(pdfData.error)}
							/>
						</div>
					)}
				</OrderDetailsHeaderRow>
				<DetailsHeaderContent>
					{isConfirmationPage && (
						<div>
							<SectionTitle>Order Date</SectionTitle>
							<SectionDescription>{dateFormatTwoDigitDays(String(todayDate))}</SectionDescription>
						</div>
					)}
					<div>
						<SectionTitle>PO #</SectionTitle>
						<SectionDescription>
							{shippingDetails.poNumber || UnavailableDataPlaceholderText}
						</SectionDescription>
					</div>
					<div>
						<SectionTitle>Order Name</SectionTitle>
						<SectionDescription>
							{shippingDetails.jobName || UnavailableDataPlaceholderText}
						</SectionDescription>
					</div>
					<div>
						<SectionTitle>{TotalItemsText}</SectionTitle>
						<SectionDescription>{totalItems}</SectionDescription>
					</div>
				</DetailsHeaderContent>
				<Divider />
				<DetailsBoxTitle>Ship To</DetailsBoxTitle>
				<ReviewShippingDetails>
					<ReviewShippingDetailsRow>
						<div>
							<SectionTitle>Address</SectionTitle>
							<SectionDescription>{formatAddress(shippingAddress)}</SectionDescription>
						</div>
						<div>
							<SectionTitle>Delivery Contact</SectionTitle>
							<SectionDescription>
								{shippingDetails?.phoneNumber || UnavailableDataPlaceholderText}
							</SectionDescription>
							<SectionDescription>
								{shippingDetails?.email || UnavailableDataPlaceholderText}
							</SectionDescription>
						</div>
					</ReviewShippingDetailsRow>
					<ReviewShippingDetailsRow>
						<AccountBillToBox>
							<DetailsBoxTitle>Account #</DetailsBoxTitle>
							<SectionDescription>{formatAccountName(selectedMerchAccount)}</SectionDescription>
						</AccountBillToBox>
						<AccountBillToBox>
							<DetailsBoxTitle>Bill To</DetailsBoxTitle>
							{Boolean(selectedMerchAccount?.billTos?.[0]) && (
								<SectionDescription>
									{displayBillToInfo(selectedMerchAccount!.billTos![0])}
								</SectionDescription>
							)}
						</AccountBillToBox>
					</ReviewShippingDetailsRow>
				</ReviewShippingDetails>
			</DetailsBox>
			<CartItemDetails
				showBanner={false}
				showInfoBanner={true}
				cartItems={cartItems}
				userRole={user?.role}
				subTotal={getTotalPrice()}
				isReviewOrConfirmation
			/>
		</>
	);
};
